/**
 * useBreakpoint - Responsive breakpoint hook
 * Mobile:  0 - 639px    (phone portrait)
 * Tablet:  640 - 1023px (phone landscape, small tablets)
 * Desktop: 1024px+      (standard desktop, large tablets)
 */
import { useState, useEffect } from 'react'

export type Breakpoint = 'mobile' | 'tablet' | 'desktop'

const BREAKPOINTS = {
  mobile: 640,
  tablet: 1024
}

function getBreakpoint(): Breakpoint {
  if (typeof window === 'undefined') return 'desktop'
  const width = window.innerWidth
  if (width < BREAKPOINTS.mobile) return 'mobile'
  if (width < BREAKPOINTS.tablet) return 'tablet'
  return 'desktop'
}

export function useBreakpoint(): Breakpoint {
  const [breakpoint, setBreakpoint] = useState<Breakpoint>(() => getBreakpoint())

  useEffect(() => {
    const update = () => setBreakpoint(getBreakpoint())
    window.addEventListener('resize', update)
    return () => window.removeEventListener('resize', update)
  }, [])

  return breakpoint
}

export function useIsMobile(): boolean {
  return useBreakpoint() === 'mobile'
}

export function useIsTablet(): boolean {
  const bp = useBreakpoint()
  return bp === 'tablet'
}

export function useIsTabletOrMobile(): boolean {
  const bp = useBreakpoint()
  return bp === 'mobile' || bp === 'tablet'
}

export function useIsDesktop(): boolean {
  return useBreakpoint() === 'desktop'
}

export function useWindowSize() {
  const [size, setSize] = useState(() => ({
    width: typeof window !== 'undefined' ? window.innerWidth : 1200,
    height: typeof window !== 'undefined' ? window.innerHeight : 800
  }))

  useEffect(() => {
    const update = () => {
      setSize({ width: window.innerWidth, height: window.innerHeight })
    }
    window.addEventListener('resize', update)
    return () => window.removeEventListener('resize', update)
  }, [])

  return size
}
